import { Component, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { delay, map, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ButtonComponent,
  DialogRefService,
  InputComponent,
  SearchSelectComponent,
  SelectComponent,
} from '@group24ag/flowkit';

interface User {
  id: number;
  name: string;
}

@Component({
  selector: 'app-dialog-new-task',
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    InputComponent,
    SelectComponent,
    SearchSelectComponent,
  ],
  template: `
    <div class="flex flex-col gap-4">
      <ng-template #prioritySelectedTpl let-item="item">{{ item }}</ng-template>
      <ng-template #priorityOptionTpl let-option="option">
        {{ option }}
      </ng-template>
      <ng-template #userSelectedTpl let-item="item">
        {{ item.name }}
      </ng-template>
      <ng-template #userOptionTpl let-option="option">
        {{ option.name }}
      </ng-template>

      <flk-input
        label="Task name"
        placeholder="Enter a task name"
        [formControl]="nameControl" />

      <flk-select
        label="Priority"
        placeholder="Select priority"
        [options]="priorities"
        [selectedTemplate]="prioritySelectedTpl"
        [optionTemplate]="priorityOptionTpl"
        [formControl]="priorityControl" />

      <flk-search-select
        label="Assignee"
        placeholder="Search for a user"
        [fetch]="fetchUsers"
        [selectedTemplate]="userSelectedTpl"
        [optionTemplate]="userOptionTpl"
        [compareWith]="compareUsers"
        [formControl]="assigneeControl" />

      <div class="flex justify-between gap-2 pt-2">
        <button
          flkButton
          [appearance]="'secondary'"
          [variant]="'default'"
          (click)="onCancel()">
          Cancel
        </button>
        <button
          flkButton
          [disabled]="nameControl.invalid || priorityControl.invalid"
          (click)="onSubmit()">
          Create
        </button>
      </div>
    </div>
  `,
})
export class DialogNewTaskComponent implements OnInit {
  /** PROPERTIES **/
  protected readonly priorities = ['Low', 'Medium', 'High', 'Critical'];

  protected readonly nameControl = new FormControl('', Validators.required);
  protected readonly priorityControl = new FormControl<string | null>(
    null,
    Validators.required
  );
  protected readonly assigneeControl = new FormControl<User | null>(null);

  private readonly _users: User[] = [
    { id: 1, name: 'Alice Müller' },
    { id: 2, name: 'Bob Schmidt' },
    { id: 3, name: 'Carol Jensen' },
    { id: 4, name: 'David Park' },
    { id: 5, name: 'Eva Larsen' },
  ];

  protected readonly fetchUsers = (query: string | null) =>
    of(this._users).pipe(
      delay(200),
      map(users =>
        users.filter(u =>
          u.name.toLowerCase().includes((query ?? '').toLowerCase())
        )
      )
    );

  protected readonly compareUsers = (a: User, b: User) => a?.id === b?.id;

  constructor(
    private readonly dialogRef: DialogRefService<DialogNewTaskComponent>
  ) {
    // Update dialog title reactively as the user types a task name.
    this.nameControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(
        name =>
          (this.dialogRef.title = name?.trim()
            ? `New Task — ${name}`
            : 'New Task')
      );
  }

  ngOnInit(): void {
    this.dialogRef.title = 'New Task';
  }

  protected onCancel(): void {
    this.dialogRef.close(null);
  }

  protected onSubmit(): void {
    this.dialogRef.close({
      name: this.nameControl.value,
      priority: this.priorityControl.value,
      assignee: this.assigneeControl.value,
    });
  }
}
